/*
 * flights.js
 *
 * Description: Holds 'Stores' Logic for Module:'Flights'.
 *
 * This file is part of: "http://apps.sundair.com/", "SunCrew2 - Flights"
 * Copyright 2018,2019,2020,2021,2022 Sundair GmbH
 * Contact: http://www.sundair.com/
 * @author Christian Arp <christian.arp@sundair.com>
 * @author Learek <michael.broeker@sundair.com>
 * @author Phillip Alsleben <phillip.alsleben@sundair.com>
 *
 */
import Vue from 'vue';
import axios from 'axios';

import { eventBus } from '../../../main';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CONF_SLOT_ALLOWED_DELAY } from '../../../config';
import { DateHelper } from '@bryntum/scheduler';

import { mutation, query } from 'gql-query-builder';

dayjs.extend(utc);

//-----------------------------------------------------------------------------/
// #
// #    /$$$$$$   /$$                /$$
// #   /$$__  $$ | $$               | $$
// #  | $$  \__//$$$$$$   /$$$$$$  /$$$$$$    /$$$$$$
// #  |  $$$$$$|_  $$_/  |____  $$|_  $$_/   /$$__  $$
// #   \____  $$ | $$     /$$$$$$$  | $$    | $$$$$$$$
// #   /$$  \ $$ | $$ /$$/$$__  $$  | $$ /$$| $$_____/
// #  |  $$$$$$/ |  $$$$/  $$$$$$$  |  $$$$/|  $$$$$$$
// #   \______/   \___/  \_______/   \___/   \_______/
// #
//-----------------------------------------------------------------------------/
const state = {
    // >>> flights -- scheduler ------------------------------------------------
    flights_Events: [],
    flights_Events_Filtered: [],
    flights_Resources: [],
    flights_Resources_Filtered: [],
    flights_Dependencies_Filtered: [],

    // >>> flights -- legs -----------------------------------------------------
    flights_Legs: [], // calls response
    flights_Leg_Tmp: [], //

    // >>> flights -- search/filter --------------------------------------------
    flights_FilterTerms_DaysAndCo: {
        aircrafts: [],
        days: [],
        types: [],
        deleted: false,
    },
    flights_FilterTerms_LegsAndFlightNo: {
        leg: '',
        flightNo: '',
    },

    // >>> flights -- search dates ---------------------------------------------
    flights_StartDate: null,
    flights_EndDate: null,

    // >>> flights -- OCC Mode -------------------------------------------------
    flights_OCCMode: false,
    // >>> flights -- FlightLabelContent ---------------------------------------
    flights_FlightLabelContent: 'FlightNo',
    // >>> flights --  ---------------------------------------------------------
    flights_Scheduler_Preset: 'hourPreset',

    // >>> flights -- selections -----------------------------------------------
    selectedMultiEventsEntireRecord: [],
    selectedMultiEvents: [],

    // >>> flights -- similar flights ------------------------------------------
    flights_similarFlights: {
        flights: [],
    },
    flights_similarFlights_parentFlightId: [],
    flights_similarFlights_flights: [],

    // >>> flights -- swap flights ---------------------------------------------
    flights_swapFlightsResponse: null,

    // >>> flights -- aircraftchange -------------------------------------------
    flights_aircraftchangeFrom: [],
    flights_aircraftchangeTo: [],

    // >>> flights - Seat Plan -------------------------------------------------
    flights_seatPlan: null,

    // >>> flights - Swap Logs -------------------------------------------------
    flights_swapLogs: [],

    // >>> flights - Block Hours -----------------------------------------------
    flights_blockHours: [],
    // >>> flights - Movement -------------------------------------------------
    flights_movement: [],
    // >>> flights - Movement -------------------------------------------------
    flights_slotWarnings: [],
    flights_rotationAndChain_slotWarnings: [],
};

//-----------------------------------------------------------------------------/
// #
// #    /$$$$$$              /$$     /$$
// #   /$$__  $$            | $$    | $$
// #  | $$  \__/  /$$$$$$  /$$$$$$ /$$$$$$    /$$$$$$   /$$$$$$  /$$$$$$$
// #  | $$ /$$$$ /$$__  $$|_  $$_/|_  $$_/   /$$__  $$ /$$__  $$/$$_____/
// #  | $$|_  $$| $$$$$$$$  | $$    | $$    | $$$$$$$$| $$  \__/  $$$$$$
// #  | $$  \ $$| $$_____/  | $$ /$$| $$ /$$| $$_____/| $$      \____  $$
// #  |  $$$$$$/|  $$$$$$$  |  $$$$/|  $$$$/|  $$$$$$$| $$      /$$$$$$$/
// #   \______/  \_______/   \___/   \___/   \_______/|__/     |_______/
// #
//-----------------------------------------------------------------------------/
const getters = {
    /***************************************************************************
     * Getters: Events/Resources
     ***************************************************************************/
    /***************************************************************************
     * get_flights_Events()
     *
     * Desc: Return Events.
     * Note: Events === Flights
     *
     */
    get_flights_Events: (state) => {
        return state.flights_Events;
    }, // eof get_flights_Events()

    /***************************************************************************
     * get_flights_Events_Filtered()
     *
     * Desc: Return Events.
     * Note: Events === Flights
     *
     */
    get_flights_Events_Filtered: (state) => {
        return state.flights_Events_Filtered;
    }, // eof get_flights_Events_Filtered()

    /***************************************************************************
     * get_flights_Resources()
     *
     * Desc: Return Resources.
     * Note: Resources === Aircrafts
     *
     */
    get_flights_Resources: (state) => {
        return state.flights_Resources;
    }, // eof get_flights_Resources()

    /***************************************************************************
     * get_flights_Resources_Filtered()
     *
     * Desc: Return filtered Resources.
     * Note: Resources === Aircrafts
     * Note: First sort: by name and capacity
     * Note: Second sort: split between SUNDAIR, FLYAIR and SUBs
     *
     */
    get_flights_Resources_Filtered: (state) => {
        // sort by capacity and name
        state.flights_Resources_Filtered = state.flights_Resources_Filtered.sort((a, b) => {
            if (a.capacityY > b.capacityY) {
                return -1;
            }
            if (a.capacityY < b.capacityY) {
                return 1;
            }

            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
        });

        // -1 = a wins
        // 1  = b wins

        // "sort" by company to "split" between SUNDAIR and FLYAIR
        // everything with "SUB" goes to the bottom
        state.flights_Resources_Filtered = state.flights_Resources_Filtered.sort((a, b) => {
            // FLYAIR and SUNDAIR
            if (a.name.includes('SUB') === false && b.name.includes('SUB') === false) {
                if (a.name.startsWith('FlyAir') === true && b.name.startsWith('FlyAir') === true) {
                    return 0;
                }
                if (a.name.startsWith('FlyAir') === false && b.name.startsWith('FlyAir') === true) {
                    return -1;
                }
                if (a.name.startsWith('FlyAir') === true && b.name.startsWith('FlyAir') === false) {
                    return 1;
                }
            }
            if (a.name.includes('SUB') === false && b.name.includes('SUB') === false) {
                if (a.name.startsWith('D-') === true && b.name.startsWith('D-') === true) {
                    return 0;
                }
                if (a.name.startsWith('D-') === false && b.name.startsWith('D-') === true) {
                    return -1;
                }
                if (a.name.startsWith('D-') === true && b.name.startsWith('D-') === false) {
                    return 1;
                }
            }

            // SUBs
            if (a.name.includes('SUB') === true && a.name > b.name) {
                return 1;
            }
            if (b.name.includes('SUB') === true && a.name < b.name) {
                return -1;
            }
        });

        return state.flights_Resources_Filtered;
    }, // eof get_flights_Resources_Filtered()

    /***************************************************************************
     * get_flights_Dependencies_Filtered()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_Dependencies_Filtered: (state) => {
        return state.flights_Dependencies_Filtered;
    }, // eof get_flights_Dependencies_Filtered()

    /***************************************************************************
     * get_flights_StartDate()
     *
     * Desc: Return StartDate of calendar / search field.
     * Note:
     *
     */
    get_flights_StartDate: (state) => {
        return state.flights_StartDate;
    }, // eof get_flights_StartDate()

    /***************************************************************************
     * get_flights_EndDate()
     *
     * Desc: Return EndDate of calendar / search field.
     * Note:
     *
     */
    get_flights_EndDate: (state) => {
        return state.flights_EndDate;
    }, // eof get_flights_EndDate()

    /***************************************************************************
     * get_flights_OCCMode()
     *
     * Desc: Return OCC Mode.
     * Note:
     *
     */
    get_flights_OCCMode: (state) => {
        return state.flights_OCCMode;
    }, // eof get_flights_OCCMode()

    /***************************************************************************
     * get_flights_FlightLabelContent()
     *
     * Desc: Return Flight Label Content.
     * Note:
     *
     */
    get_flights_FlightLabelContent: (state) => {
        return state.flights_FlightLabelContent;
    }, // eof get_flights_FlightLabelContent()

    /***************************************************************************
     * get_flights_Scheduler_Preset()
     *
     * Desc: Return Scheduler Preset.
     * Note:
     *
     */
    get_flights_Scheduler_Preset: (state) => {
        return state.flights_Scheduler_Preset;
    }, // eof get_flights_Scheduler_Preset()

    /***************************************************************************
     * Getters: 'Selections' - Get selected Multi Events/Resources.
     ***************************************************************************/
    /***************************************************************************
     * get_selectedMultiEvents()
     *
     * Desc: Return selected Multi Events.
     * Note: paylod:: e.g.: 'COMMERCIAL' ??
     * Note: Getter with Payload.
     *
     */
    get_selectedMultiEvents: (state) => (payload) => {
        if (payload !== undefined && payload !== null) {
            return state.selectedMultiEvents.filter((x) => x.name === payload);
        } else {
            return state.selectedMultiEvents;
        }
    }, // eof get_selectedMultiEvents()

    /***************************************************************************
     * get_selectedMultiEventsEntireRecord()
     *
     * Desc: Return selected Multi Events. (entire Records)
     *
     */
    get_selectedMultiEventsEntireRecord: (state) => {
        return state.selectedMultiEventsEntireRecord;
    }, // eof get_selectedMultiEventsEntireRecord()

    /***************************************************************************
     * get_similarFlights()
     *
     * Desc:
     *
     */
    get_similarFlights: (state) => {
        return state.flights_similarFlights;
    }, // eof get_similarFlights()

    /***************************************************************************
     * get_flights_lastDateOfSimilarFlights()
     *
     * Desc: Returns last date of the similar flights array.
     * Note:
     *
     */
    get_flights_lastDateOfSimilarFlights: (state) => {
        let lastDate = dayjs().utc();

        state.flights_similarFlights.flights.forEach((flight) => {
            if (lastDate.isBefore(dayjs(flight.legInfo.dep_sch_time).utc())) {
                lastDate = dayjs(flight.legInfo.dep_sch_time).utc();
            }
        });

        return lastDate;
    }, // eof get_flights_lastDateOfSimilarFlights()

    /***************************************************************************
     * flights_similarFlights_parentFlightId()
     *
     * Desc: Returns last date of the similar flights array.
     * Note:
     *
     */
    get_flights_similarFlights_parentFlightId: (state) => {
        return state.flights_similarFlights_parentFlightId;
    }, // eof get_flights_similarFlights_parentFlightId()

    /***************************************************************************
     * get_flights_similarFlights_flights()
     *
     * Desc: Returns last date of the similar flights array.
     * Note:
     *
     */
    get_flights_similarFlights_flights: (state) => {
        return state.flights_similarFlights_flights;
    }, // eof get_flights_similarFlights_flights()

    /***************************************************************************
     * get_flights_swapFlightsResponse()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_swapFlightsResponse: (state) => {
        return state.flights_swapFlightsResponse;
    }, // eof get_flights_swapFlightsResponse()

    /***************************************************************************
     * get_flights_aircraftchangeFrom()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_aircraftchangeFrom: (state) => {
        return state.flights_aircraftchangeFrom;
    }, // eof get_flights_aircraftchangeFrom()

    /***************************************************************************
     * get_flights_aircraftchangeTo()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_aircraftchangeTo: (state) => {
        return state.flights_aircraftchangeTo;
    }, // eof get_flights_aircraftchangeTo()

    /***************************************************************************
     * Getters: 'SeatPlan'
     ***************************************************************************/
    /***************************************************************************
     * get_flights_seatPlan()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_seatPlan: (state) => {
        return state.flights_seatPlan;
    }, // eof get_flights_seatPlan()

    /***************************************************************************
     * Getters: 'Movement'
     ***************************************************************************/
    /***************************************************************************
     * get_flights_movement()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_movement: (state) => {
        return state.flights_movement;
    }, // eof get_flights_movement()

    /***************************************************************************
     * Getters: 'Reports'
     ***************************************************************************/
    /***************************************************************************
     * get_flights_swapLogs()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_swapLogs: (state) => {
        return state.flights_swapLogs;
    }, // eof get_flights_swapLogs()

    /***************************************************************************
     * get_flights_blockHours()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_blockHours: (state) => {
        return state.flights_blockHours;
    }, // eof get_flights_blockHours()

    /***************************************************************************
     * get_flights_slotWarnings()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_slotWarnings: (state) => {
        return state.flights_slotWarnings;
    }, // eof get_flights_slotWarnings()

    /***************************************************************************
     * get_flights_rotationAndChain_slotWarnings()
     *
     * Desc:
     * Note:
     *
     */
    get_flights_rotationAndChain_slotWarnings: (state) => {
        return state.flights_rotationAndChain_slotWarnings;
    }, // eof get_flights_rotationAndChain_slotWarnings()
};

//-----------------------------------------------------------------------------/
// #
// #   /$$      /$$             /$$                /$$
// #  | $$$    /$$$            | $$               | $$
// #  | $$$$  /$$$$ /$$   /$$ /$$$$$$   /$$$$$$  /$$$$$$    /$$$$$$
// #  | $$ $$/$$ $$| $$  | $$|_  $$_/  |____  $$|_  $$_/   /$$__  $$
// #  | $$  $$$| $$| $$  | $$  | $$     /$$$$$$$  | $$    | $$$$$$$$
// #  | $$\  $ | $$| $$  | $$  | $$ /$$/$$__  $$  | $$ /$$| $$_____/
// #  | $$ \/  | $$|  $$$$$$/  |  $$$$/  $$$$$$$  |  $$$$/|  $$$$$$$
// #  |__/     |__/ \______/    \___/  \_______/   \___/   \_______/
// #
//-----------------------------------------------------------------------------/
const mutations = {
    /***************************************************************************
     * Mutations: Events/Resources
     ***************************************************************************/
    /***************************************************************************
     * mut_call_getLegsInfo()
     *
     * Desc: Build 'events & 'resources' &  'dependencies' after getLegsInfo Call.
     * Note:
     * Note: COMMERCIAL  (67aaf9)
     * Note: POSITIONAL  (P, 67aaf9 (old), ?? (New) flightModus P)
     * Note: TECHNICAL   (d11149, flightModus E)
     * Note: FERRY       (F, c7ddf7, flightModus F)
     *
     * Note: Filter Legs 'AC & ''BA' out.
     *
     */
    mut_call_getLegsInfo: (state, payload) => {
        const tmpAircraftConfigs = payload.context.rootState.shared.people_aircraftConfigs_complaintManagement,
            tmpResources = [];

        state.flights_Legs = [];
        state.flights_Events_Filtered = [];

        // >>> Legs into State ----------------------------
        state.flights_Legs = payload.payload.filter((leg) => leg.name !== 'AC' && leg.name !== 'BA'); // save all

        // >>> Aircraft => Build Resources ----------------
        tmpAircraftConfigs.forEach((aircraft) => {
            tmpResources.push({
                id: aircraft.idAircraft,
                idAircraft: aircraft.id,
                name: aircraft.code,
                code: aircraft.code,
                type: 'Sales',
                eventColor: '#2d3e52',
                capacityC: aircraft.capacityC,
                capacityY: aircraft.capacityY,
                flightTime: 0,
                active: aircraft.active,
            });
        });
        state.flights_Resources = tmpResources;

        // >>> Mutations ----------------------------------
        payload.context.commit('mut_refresh_flight_state', payload);
    }, // eof mut_call_getLegsInfo()

    /***************************************************************************
     * mut_call_getSlotWarnings()
     *
     * Desc: 1 Flight has 2 Slots (departure and arrival)
     * Desc: Groups 'slots' by 'idFlightSegment' & 'direction'.
     * Note: As we get one slot several times from /slots/list (for each change one entry)
     * Note: we want to group same slots, so that we have a list on unique slots
     * Note: with grouped changes into new array 'slotChangeLogs' (list of changes for the slot)
     * Note: TODO: put into global function (is used in flights AND slotmanagment)
     *
     */
    mut_call_getSlotWarnings: (state, payload) => {
        const groupedSlots = [];
        let filteredSlots = [],
            filteredGroupedSlots = [];

        /***************************************************************************
         * helper_buildSlotProperty()
         *
         * Desc: Builds the slot message
         * Note:
         *
         */
        const helper_buildSlotProperty = (slot) => {
            let slotMessage = '';

            if (slot.direction === 'DEPARTURE') {
                slotMessage = `${dayjs(slot.slotTime).utc().format('HHmm')}${slot.airport.aptCode}`;
            } else {
                slotMessage = `${slot.airport.aptCode}${dayjs(slot.slotTime).utc().format('HHmm')}`;
            }

            return slotMessage;
        }; // helper_buildSlotProperty

        // filter inactive aircrafts
        filteredSlots = payload.payload.filter((slot) =>
            payload.context.rootState.shared.people_aircraftConfigs.find((a) => a.idAircraft === slot.aircraftType)
        );
        filteredSlots = filteredSlots.filter(
            (slot) =>
                slot.type !== 'changeAircraft' ||
                payload.context.rootState.shared.people_aircraftConfigs.find((a) => a.id === +slot.newValue)
        );
        filteredSlots = filteredSlots.filter(
            (slot) =>
                slot.type !== 'changeAircraft' ||
                payload.context.rootState.shared.people_aircraftConfigs.find((a) => a.id === +slot.oldValue)
        );

        // >>> Group by 'idFlightSegment' & 'direction' ------------------------
        filteredSlots.forEach((slot) => {
            const keySlot = `${slot.idFlightSegment}-${slot.direction}`, // create key
                indexSlot = groupedSlots.findIndex((slot) => slot.key === keySlot); // find index of slot

            slot.airportSlot = payload.context.rootState.airports.airports_airports.find(
                (a) => a.iataCode === slot.airportSlot
            );
            slot.airport = payload.context.rootState.airports.airports_airports.find(
                (a) => a.iataCode === slot.airport
            );
            slot.aircraftType = payload.context.rootState.shared.people_aircraftConfigs.find(
                (a) => a.idAircraft === slot.aircraftType
            );
            if (slot.type === 'changeAircraft') {
                // get the whole aircraft object
                slot.newValue = payload.context.rootState.shared.people_aircraftConfigs.find(
                    (a) => a.id === +slot.newValue
                );
                slot.oldValue = payload.context.rootState.shared.people_aircraftConfigs.find(
                    (a) => a.id === +slot.oldValue
                );
            }
            if (slot.type === 'airport') {
                // get the whole airport object
                slot.newValue = payload.context.rootState.airports.airports_airports.find(
                    (a) => a.iataCode === slot.newValue
                );
                slot.oldValue = payload.context.rootState.airports.airports_airports.find(
                    (a) => a.iataCode === slot.oldValue
                );
            }
            if (slot.type === 'retiming') {
                // we always need the original slotTime as oldValue
                slot.oldValue = slot.slotTime;
            }
            slot.date = dayjs(slot.slotTime).utc().format('YYYY-MM-DD');
            slot.time = dayjs(slot.slotTime).utc().format('HH:mm');
            slot.slot = helper_buildSlotProperty(slot);

            // extend slot object with 'key' property
            slot = {
                key: keySlot,
                ...slot,
            };
            // if slot already exists
            if (indexSlot >= 0) {
                const keyChange = slot.type,
                    indexChange = groupedSlots[indexSlot].slotChangeLogs.findIndex(
                        (slotChangeLog) => slotChangeLog.type === keyChange
                    );
                // if slotChangeLog already exists
                if (indexChange >= 0) {
                    // update slotChangeLog
                    groupedSlots[indexSlot].slotChangeLogs[indexChange].newValue = slot.newValue;
                    groupedSlots[indexSlot].slotChangeLogs[indexChange].changeDate = slot.changeDate;
                    groupedSlots[indexSlot].slotChangeLogs[indexChange].slotIds.push(slot.id);
                    // if slotChangeLog does not exist
                } else {
                    // create new slotChangeLog
                    groupedSlots[indexSlot].slotChangeLogs.push({
                        type: slot.type,
                        oldValue: slot.oldValue,
                        newValue: slot.newValue,
                        changeDate: slot.changeDate,
                        slotIds: [slot.id],
                    });
                }
                // if slot does not exist
            } else {
                // create new slotChangeLog
                slot.slotChangeLogs = [
                    {
                        type: slot.type,
                        oldValue: slot.oldValue,
                        newValue: slot.newValue,
                        changeDate: slot.changeDate,
                        slotIds: [slot.id],
                    },
                ];
                // create new slot
                groupedSlots.push(slot);
            }
        });

        // filter unnecessary slot changes which has no real changed (happens if you change smth on a flight and change it back to original)
        filteredGroupedSlots = groupedSlots.map((slot) => {
            slot.slotChangeLogs = slot.slotChangeLogs.filter((s) => s.newValue !== s.oldValue);
            return slot;
        });
        filteredGroupedSlots = filteredGroupedSlots.filter((s) => s.status === 'DELETE' || s.slotChangeLogs.length > 0);
        state.flights_slotWarnings = filteredGroupedSlots.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));

        // >>> Mutations ----------------------------------
        payload.context.commit('mut_slotsTable_buildRotationsAndChains');
    }, // eof mut_call_getSlotWarnings()

    /***************************************************************************
     * mut_slotsTable_buildRotationsAndChains()
     *
     * Desc: Group single slots into rotations and chains
     * Note:
     * @param {Object} state - The state object containing the slots table data.
     * @param {Object} payload - The payload object containing additional data.
     */
    mut_slotsTable_buildRotationsAndChains: (state) => {
        const me = this,
            keysRotation = ['airportSlot', 'airport', 'slotTime', 'direction'],
            keysChain = ['flightNo', 'airportSlot', 'airport', 'slotTime'];

        /***************************************************************************
         * helper_buildRotationsAndChains_buildWarnings()
         *
         * Desc:
         * Note:
         *
         */
        const helper_buildRotationsAndChains_buildWarnings = (slot) => {
            const warnings = [];
            let warning = {};

            if (dayjs(slot.slotTime).isSameOrBefore(dayjs().add(2, 'day'), 'day')) {
                warning = {
                    icon: 'mdi-clock-alert',
                    text: 'Slot expires',
                };
                warnings.push(warning);
            }

            return warnings;
        }; // eo function helper_buildRotationsAndChains_buildWarnings()

        /***********************************************************************
         * getNewSlotValue
         *
         * Desc: Takes change logs and change type and returns the new value
         * Note:
         *
         */
        const getNewSlotValue = (changeLogs, type) => {
            let foundLog = null;
            foundLog = changeLogs.find((log) => log.type === type);

            return foundLog ? foundLog.newValue : '';
        }; // eo function getNewSlotValue()

        // combine rotations
        const rotations = state.flights_slotWarnings.reduce((acc, obj) => {
            const value = keysRotation.map((key) => obj[key]);
            const index = acc.findIndex((item) =>
                keysRotation.every((key, i) => {
                    if (key === 'slotTime') {
                        return Math.abs(dayjs(value[i]).diff(dayjs(item[key]), 'hours')) < 14;
                    } else if (key === 'direction') {
                        return item[key] !== value[i];
                    } else {
                        return item[key] === value[i];
                    }
                })
            );

            if (index >= 0) {
                acc[index].flightNo.push(obj.flightNo);
                acc[index].slot.push(obj.slot);
                acc[index].status += acc[index].status !== obj.status ? ` / ${obj.status}` : '';
                acc[index].items.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));
                acc[index].items.push(obj);
                // acc[index].changedAircraft += ` ${getNewSlotValue(obj.slotChangeLogs, 'changeAircraft').code}`;
                acc[index].changedFlightNo.push(
                    getNewSlotValue(obj.slotChangeLogs, 'flightNo')
                        ? getNewSlotValue(obj.slotChangeLogs, 'flightNo')
                        : ''
                );
                acc[index].changedAirport = getNewSlotValue(obj.slotChangeLogs, 'airport')
                    ? getNewSlotValue(obj.slotChangeLogs, 'airport')
                    : obj.airport.aptCode;
                acc[index].changedTime = getNewSlotValue(obj.slotChangeLogs, 'retiming')
                    ? Vue.prototype.$dayjsLocalToUTC(getNewSlotValue(obj.slotChangeLogs, 'retiming'), 'HHmm')
                    : Vue.prototype.$dayjsLocalToUTC(obj.slotTime, 'HHmm');
                acc[index].changedSlot.push(
                    getNewSlotValue(obj.slotChangeLogs, 'airport') || getNewSlotValue(obj.slotChangeLogs, 'retiming')
                        ? `${
                              obj.direction === 'DEPARTURE'
                                  ? acc[index].changedTime + acc[index].changedAirport
                                  : acc[index].changedAirport + acc[index].changedTime
                          }`
                        : ''
                );
                acc.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));
            } else {
                const item = {};
                keysRotation.forEach((key, i) => {
                    item[key] = value[i];
                });
                item.id = obj.id;
                item.flightNo = [obj.flightNo];
                item.slot = [obj.slot];
                item.aircraftType = obj.aircraftType;
                item.status = obj.status;
                item.warnings = helper_buildRotationsAndChains_buildWarnings(obj);
                item.changedAircraft = getNewSlotValue(obj.slotChangeLogs, 'changeAircraft').code;
                item.changedFlightNo = [getNewSlotValue(obj.slotChangeLogs, 'flightNo')];
                item.changedTime = getNewSlotValue(obj.slotChangeLogs, 'retiming')
                    ? Vue.prototype.$dayjsLocalToUTC(getNewSlotValue(obj.slotChangeLogs, 'retiming'), 'HHmm')
                    : Vue.prototype.$dayjsLocalToUTC(obj.slotTime, 'HHmm');
                item.changedAirport = getNewSlotValue(obj.slotChangeLogs, 'airport')
                    ? getNewSlotValue(obj.slotChangeLogs, 'airport').aptCode
                    : obj.airport.aptCode;
                item.changedSlot = [
                    getNewSlotValue(obj.slotChangeLogs, 'airport') || getNewSlotValue(obj.slotChangeLogs, 'retiming')
                        ? `${
                              obj.direction === 'DEPARTURE'
                                  ? item.changedTime + item.changedAirport
                                  : item.changedAirport + item.changedTime
                          }`
                        : '',
                ];
                item.items = [obj];
                acc.push(item);
                acc.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));
            }

            return acc;
        }, []);

        // combine chains
        const chains = rotations.reduce((acc, obj) => {
            const value = keysChain.map((key) => obj[key]);
            const index = acc.findIndex((item) =>
                keysChain.every((key, i) => {
                    if (key === 'slotTime') {
                        return Math.abs(dayjs(value[i]).diff(dayjs(item[key]), 'days')) === 7;
                    } else if (key === 'flightNo') {
                        return JSON.stringify(item[key]) === JSON.stringify(value[i]);
                    } else {
                        return item[key] === value[i];
                    }
                })
            );

            if (index >= 0) {
                acc[index].dateRange = `${dayjs(acc[index].items[0].slotTime).format('DD.MM.YYYY')} - ${dayjs(
                    obj.slotTime
                ).format('DD.MM.YYYY')}`;
                // acc[index].status += ` / ${obj.status}`;
                acc[index].items.push(obj);
                acc[index].items.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));
                acc.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));
            } else {
                const item = {};
                keysChain.forEach((key, i) => {
                    item[key] = value[i];
                });
                item.id = obj.id;
                item.dateRange = dayjs(obj.slotTime).format('DD.MM.YYYY');
                item.slot = obj.slot;
                item.aircraftType = obj.aircraftType;
                item.status = obj.status;
                item.warnings = obj.warnings;
                item.changedAircraft = obj.changedAircraft;
                item.changedFlightNo = obj.changedFlightNo;
                item.changedAirport = obj.changedAirport;
                item.changedTime = obj.changedTime;
                item.changedSlot = obj.changedSlot;
                item.items = [obj];
                acc.push(item);
                acc.sort((a, b) => new Date(a.slotTime) - new Date(b.slotTime));
            }

            return acc;
        }, []);

        state.flights_rotationAndChain_slotWarnings = chains;
    }, // mut_slotsTable_buildRotationsAndChains

    /***************************************************************************
     * mut_call_getLegsInfo_warnings()
     *
     * Desc: Refreshes the collisionCheck Node of every flight
     * Note: Establish communication between flights
     *
     */
    mut_call_getLegsInfo_warnings: (state, payload) => {
        state.flights_Legs = state.flights_Legs.map((flight) => {
            const foundFlightWithWarning = payload.payload.find((p) => p.idFlight === flight.idFlight);
            if (foundFlightWithWarning !== undefined) {
                flight.collisionCheck = foundFlightWithWarning.collisionCheck;
            } else {
                flight.collisionCheck = [];
            }
            return flight;
        });

        // >>> Mutations ----------------------------------
        payload.context.commit('mut_refresh_flight_state', payload);
    }, // eof mut_call_getLegsInfo_warnings()

    /***************************************************************************
     * mut_call_deleteFlight()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_deleteFlight: (state, payload) => {
        state.flights_Legs.forEach((leg) => {
            if (payload.payload.includes(leg.idFlight)) {
                // payload.payload = idFlight of deleted flight
                leg.deleted = dayjs().format('YYYY-MM-DDTHH:mm:ssZ');
            }
        });

        // >>> Mutations ----------------------------------
        payload.context.commit('mut_refresh_flight_state', payload);
        payload.context.dispatch('actn_call_getLegsInfo_warnings');
        payload.context.dispatch('actn_call_getSlotWarnings', {
            fromDate: dayjs(state.flights_StartDate).format('YYYY-MM-DDT00:00:00[Z]'),
            toDate: dayjs(state.flights_EndDate).format('YYYY-MM-DDT23:59:59[Z]'),
        });
    }, // eof mut_call_deleteFlight()

    /***************************************************************************
     * mut_call_reActicateFlight()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_reActicateFlight: (state, payload) => {
        state.flights_Legs.forEach((leg) => {
            if (payload.payload.includes(leg.idFlight)) {
                // payload.payload = idFlight of reactivated flight
                leg.deleted = null;
            }
        });

        // >>> Mutations ----------------------------------
        payload.context.commit('mut_refresh_flight_state', payload);
        payload.context.dispatch('actn_call_getLegsInfo_warnings');
        payload.context.dispatch('actn_call_getSlotWarnings', {
            fromDate: dayjs(state.flights_StartDate).format('YYYY-MM-DDT00:00:00[Z]'),
            toDate: dayjs(state.flights_EndDate).format('YYYY-MM-DDT23:59:59[Z]'),
        });
    }, // eof mut_call_reActicateFlight()

    /***************************************************************************
     * mut_call_getLegsInfo_SimilarFlights()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_getLegsInfo_SimilarFlights: (state, payload) => {
        const flights = [];

        payload.payload.forEach((p) => {
            p.aptFrom.iataCode = p.aptFrom.aptCode;
            p.aptTo.iataCode = p.aptTo.aptCode;

            if (dayjs(p.departureTime).utc().get('date') !== dayjs(p.arrivalTime).utc().get('date')) {
                p.overnight = true;
            }
            if (dayjs(p.departureTime).utc().get('date') === dayjs(p.arrivalTime).utc().get('date')) {
                p.overnight = false;
            }
            // Slots needed?
            p.slotNeededDeparture = false;
            p.slotNeededArrival = false;

            payload.context.rootState.airports.airports_airports.forEach((airport) => {
                if (airport.iataCode === p.aptFrom.aptCode) {
                    if (airport.coordinated === true) {
                        p.slotNeededDeparture = true;
                        p.aptFrom.coordinated = true;
                    }
                }
                if (airport.iataCode === p.aptTo.aptCode) {
                    if (airport.coordinated === true) {
                        p.slotNeededArrival = true;
                        p.aptTo.coordinated = true;
                    }
                }
            });

            // Slot Warning(s)
            p.slotWarningDeparture = false;
            p.slotWarningArrival = false;
            if (p.slotNeededDeparture === true) {
                if (p.dep_slt_time === null) {
                    p.slotWarningDeparture = true;
                } else if (
                    Math.abs(dayjs(p.dep_slt_time).utc().diff(dayjs(p.dep_sch_time).utc(), 'minutes')) >
                    CONF_SLOT_ALLOWED_DELAY
                ) {
                    p.slotWarningDeparture = true;
                }
            }

            if (p.slotNeededArrival === true) {
                if (p.arr_slt_time === null) {
                    p.slotWarningArrival = true;
                } else if (
                    Math.abs(dayjs(p.arr_slt_time).utc().diff(dayjs(p.arr_sch_time).utc(), 'minutes')) >
                    CONF_SLOT_ALLOWED_DELAY
                ) {
                    p.slotWarningArrival = true;
                }
            }

            if (p.idFlight !== payload.parentFlightId && p.deleted === null) {
                // Learek
                flights.push({
                    id: p.idFlight,
                    legInfo: p,
                    name: p.flightNo,
                    isParent: false,
                });
            }
        });

        state.flights_similarFlights = {
            parentFlightId: payload.parentFlightId,
            flights: flights,
            name: '',
        };

        state.flights_similarFlights_parentFlightId = payload.parentFlightId;
        state.flights_similarFlights_flights = flights;
    }, // eo function mut_call_getLegsInfo_SimilarFlights()

    /***************************************************************************
     * mut_calculate_Flight_Time()
     *
     * Desc: Calculates the Flight Time for each shown Resource (Aircraft)
     * Desc: after every kind of Event state change
     * Note:
     *
     */
    mut_calculate_Flight_Time: (state) => {
        const tmpFlightsResources = state.flights_Resources_Filtered;

        // reset
        tmpFlightsResources.forEach((r) => {
            r.flightTime = 0;
        });
        // goes trough every flight
        state.flights_Events_Filtered.forEach((e) => {
            // looks for the aircraft which operates the flight
            const foundResource = tmpFlightsResources.find((r) => r.id === e.resourceId);
            // if found
            if (foundResource !== undefined) {
                // adds flight times
                foundResource.flightTime += dayjs(e.legInfo.arrivalTime).diff(e.legInfo.departureTime, 'minute');
            }
        });
        // replaces resources with manipulated resources
        state.flights_Resources_Filtered = tmpFlightsResources;
    }, // eof mut_calculate_Flight_Time

    /***************************************************************************
     * Mutations: Filter SET Events/Resources, SET Dates
     ***************************************************************************/
    /***************************************************************************
     * mut_setCalendarDates()
     *
     * Desc: Sets the selected StartDate and EndDate.
     * Note: HACK: We need to set set startDate and endDate seperatly,
     * Note: because the bryntum scheduler behavior seems to be like:
     * Note: If start and endDate change at teh same moment, endDate
     * Note: will be updated a moment before startDate and this could create
     * Note: an error because endDate could be before startDate.
     *
     */
    mut_set_CalendarDates: async (state, payload) => {
        state.flights_StartDate = payload.payload.startDate.startOf('day');

        setTimeout(() => {
            // HACK
            state.flights_EndDate = payload.payload.endDate.endOf('day');
        }, 100);
    }, // eof mut_setCalendarDates()

    /***************************************************************************
     * mut_set_FilterTerms_DaysAndCo()
     *
     * Desc: Sets the Filter Terms => Days & Co.
     * Note:
     *
     */
    mut_set_FilterTerms_DaysAndCo: (state, payload) => {
        state.flights_FilterTerms_DaysAndCo = payload.payload;

        payload.context.commit('mut_refresh_flight_state', payload);
    }, // eof mut_set_FilterTerms_DaysAndCo()

    /***************************************************************************
     * mut_set_FilterTerms_LegsAndFlightNo()
     *
     * Desc: Sets the Filter Terms => Legs & FlightNo.
     * Note:
     *
     */
    mut_set_FilterTerms_LegsAndFlightNo: (state, payload) => {
        state.flights_FilterTerms_LegsAndFlightNo = payload.payload;

        payload.context.commit('mut_refresh_flight_state', payload);
    }, // eof mut_set_FilterTerms_DaysAndCo()

    /***************************************************************************
     * Mutations: Filter Events/Resources
     ***************************************************************************/
    /***************************************************************************
     * mut_filter_Resources()
     *
     * Desc: Filters the selected Resources (Aircrafts).
     * Note:
     *
     */
    mut_filter_Resources: (state) => {
        const filterAircrafts = (resource) => {
            let applyFilter = false;

            if (state.flights_FilterTerms_DaysAndCo && state.flights_FilterTerms_DaysAndCo.aircrafts.length > 0) {
                if (state.flights_FilterTerms_DaysAndCo.aircrafts.includes(resource.id)) {
                    applyFilter = true;
                }
            }

            if (state.flights_FilterTerms_DaysAndCo && state.flights_FilterTerms_DaysAndCo.aircrafts.length === 0) {
                applyFilter = true;
            }

            return applyFilter;
        };

        state.flights_Resources_Filtered = state.flights_Resources.filter((resource) => filterAircrafts(resource));
    }, // eof mut_filter_Resources()

    /***************************************************************************
     * mut_filter_Events()
     *
     * Desc: Filters the selected Events.
     * Note:
     *
     */
    mut_filter_Events: (state) => {
        // >>> -------------------------------------------- Day&Co
        const filterDays = (event) => {
            let applyFilter = false;

            if (state.flights_FilterTerms_DaysAndCo && state.flights_FilterTerms_DaysAndCo.days.length > 0) {
                if (state.flights_FilterTerms_DaysAndCo.days.includes(dayjs(event.startDate).get('day'))) {
                    applyFilter = true;
                }
            }
            if (state.flights_FilterTerms_DaysAndCo && state.flights_FilterTerms_DaysAndCo.days.length === 0) {
                applyFilter = true;
            }

            return applyFilter;
        };

        // >>> -------------------------------------------- Leg&FlightNo
        const filterLeg = (event) => {
            let applyFilter = false;

            if (state.flights_FilterTerms_LegsAndFlightNo && state.flights_FilterTerms_LegsAndFlightNo.leg.length > 0) {
                if (event.legInfo.leg.toUpperCase().includes(state.flights_FilterTerms_LegsAndFlightNo.leg)) {
                    applyFilter = true;
                }
            }
            if (
                state.flights_FilterTerms_LegsAndFlightNo &&
                state.flights_FilterTerms_LegsAndFlightNo.leg.length === 0
            ) {
                applyFilter = true;
            }

            return applyFilter;
        };

        const filterFlightNo = (event) => {
            let applyFilter = false;

            if (
                state.flights_FilterTerms_LegsAndFlightNo &&
                state.flights_FilterTerms_LegsAndFlightNo.flightNo.length > 0
            ) {
                if (event.legInfo.flightNo.toUpperCase().includes(state.flights_FilterTerms_LegsAndFlightNo.flightNo)) {
                    applyFilter = true;
                }
            }
            if (
                state.flights_FilterTerms_LegsAndFlightNo &&
                state.flights_FilterTerms_LegsAndFlightNo.flightNo.length === 0
            ) {
                applyFilter = true;
            }

            return applyFilter;
        };

        const filterDeleted = (event) => {
            let applyFilter = false;

            if (state.flights_FilterTerms_DaysAndCo && state.flights_FilterTerms_DaysAndCo.deleted === true) {
                applyFilter = true; // deleted filter set to true means "show all flights"
            }
            if (state.flights_FilterTerms_DaysAndCo && state.flights_FilterTerms_DaysAndCo.deleted === false) {
                if (event.legInfo.deleted === null) {
                    applyFilter = true;
                }
            }

            return applyFilter;
        };

        state.flights_Events_Filtered = state.flights_Events.filter((event) => filterDays(event));
        // commented out - see #126
        // // state.flights_Events_Filtered = state.flights_Events_Filtered.filter(event => filterTypes(event));
        state.flights_Events_Filtered = state.flights_Events_Filtered.filter((event) => filterDeleted(event));
        state.flights_Events_Filtered = state.flights_Events_Filtered.filter((event) => filterLeg(event));
        state.flights_Events_Filtered = state.flights_Events_Filtered.filter((event) => filterFlightNo(event));
    }, // eof mut_filter_Events()

    /***************************************************************************
     * mut_filter_Dependencies()
     *
     * Desc: Filters the Dependencies for selected Events.
     * Note:
     *
     */
    mut_filter_Dependencies: (state) => {
        state.flights_Dependencies_Filtered = state.flights_Dependencies;
    }, // eof mut_filter_Dependencies()

    /***************************************************************************
     * mut_build_Events()
     *
     * Desc: TODO: REFACTOR simply add functions
     * Note:
     *
     */
    mut_build_Events: (state, payload) => {
        const eventColors = Vue.prototype.$getEventColors(),
            tmpEvents = [];

        // >>> Legs => Build Events -----------------------
        state.flights_Legs.forEach((leg) => {
            let flightModus = '',
                getColors = null,
                flightCls = '',
                style = '',
                startDate = '',
                endDate = '',
                occDelayBar = null;

            flightModus = leg.flightModus;
            flightModus = flightModus === null ? 'NULL' : flightModus; // replace null by 'NULL' to e.g. make filter work
            flightModus = leg.flightNo.substring(0, 3) === 'ACV' ? 'V' : flightModus; // special case for Aircraft Visits. FlightModus is based on FlightNo Pattern.
            flightModus = leg.flightNo.substring(0, 3) === 'AOG' ? 'AOG' : flightModus; // special case for Aircraft On Ground. FlightModus is based on FlightNo Pattern.
            getColors = eventColors.find((colors) => colors.flighttype === flightModus);
            startDate = new Date(dayjs(leg.departureTime).utc().format('YYYY/MM/DD HH:mm')); // TODO: SCHEDULE OR ACTUAL???
            endDate = new Date(dayjs(leg.arrivalTime).utc().format('YYYY/MM/DD HH:mm')); // TODO: SCHEDULE OR ACTUAL???

            if (leg.flightModus === null) {
                leg.flightModus = flightModus;
            }

            // historically conditioned.. mainServiceType was once type boolean
            if (leg.mainServiceType === '0' || leg.mainServiceType === null) {
                leg.mainServiceType = 'C';
            }
            if (leg.mainServiceType === '1') {
                leg.mainServiceType = 'J';
            }

            //check if flight is overnight flight
            if (dayjs(leg.departureTime).utc().get('date') !== dayjs(leg.arrivalTime).utc().get('date')) {
                leg.overnight = true;
            }
            if (dayjs(leg.departureTime).utc().get('date') === dayjs(leg.arrivalTime).utc().get('date')) {
                leg.overnight = false;
            }

            // cls and style for the Flight
            if (
                state.flights_OCCMode &&
                dayjs(leg.departureTime).utc().isSame(dayjs().utc(), 'day') &&
                leg.flightModus === 'NULL'
            ) {
                if (dayjs(leg.arrivalTime).utc().isBefore(dayjs().utc())) {
                    flightCls = 'flight_Landed';
                } else if (dayjs(leg.departureTime).utc().isAfter(dayjs().utc())) {
                    flightCls = 'flight_Scheduled';
                } else {
                    flightCls = 'flight_In_Air';
                }
            } else {
                if (getColors) {
                    flightCls = String(getColors.cls);
                } else {
                    flightCls = 'flight_Universal';
                }
            }

            // ADD WARNING STYLES
            if (leg.deleted !== null) {
                // flight is INactive

                style = style + 'opacity: 0.5;';
            } else {
                const boxShadowStyleWidth = 4;
                let boxShadowStyleString = '';

                leg.collisionCheck.forEach((collision) => {
                    if (collision.type === 'DESTINATION AIRPORT') {
                        boxShadowStyleString =
                            boxShadowStyleString + `border-right: ${boxShadowStyleWidth}px solid orange;`;
                    }
                    if (collision.type === 'DEPARTURE AIRPORT') {
                        boxShadowStyleString =
                            boxShadowStyleString + `border-left: ${boxShadowStyleWidth}px solid orange;`;
                    }
                    if (collision.type === 'COLLISION') {
                        boxShadowStyleString =
                            boxShadowStyleString + `border-bottom: ${boxShadowStyleWidth}px solid red;`;
                    }
                    if (collision.type === 'FLIGHTNO') {
                        boxShadowStyleString =
                            boxShadowStyleString + `border-top: ${boxShadowStyleWidth}px solid yellow;`;
                    }
                });

                style += boxShadowStyleString;
            }

            leg.aircraft = payload.payload.context.rootState.shared.people_aircraftConfigs.find(
                (a) => a.id === leg.idAircraftConfig
            );

            // Get whole airport object and override leg_aptFrom/leg_aptTo to get all information
            payload.payload.context.rootState.airports.airports_airports.forEach((airport) => {
                if (airport.iataCode === leg.aptFrom.aptCode) {
                    leg.aptFrom = airport;
                }
                if (airport.iataCode === leg.aptTo.aptCode) {
                    leg.aptTo = airport;
                }
            });

            // Slot Warning(s)
            if (leg.aptFrom.coordinated && (flightModus === 'NULL' || flightModus === 'F')) {
                if (leg.dep_slt_time === null) {
                    leg.slotWarningDeparture = true;
                } else if (
                    Math.abs(dayjs(leg.dep_slt_time).diff(dayjs(leg.dep_sch_time), 'minutes')) > CONF_SLOT_ALLOWED_DELAY
                ) {
                    leg.slotWarningDeparture = true;
                } else {
                    leg.slotWarningDeparture = false;
                }
            }

            if (leg.aptTo.coordinated && (flightModus === 'NULL' || flightModus === 'F')) {
                if (leg.arr_slt_time === null) {
                    leg.slotWarningArrival = true;
                } else if (
                    Math.abs(dayjs(leg.arr_slt_time).diff(dayjs(leg.arr_sch_time), 'minutes')) > CONF_SLOT_ALLOWED_DELAY
                ) {
                    leg.slotWarningArrival = true;
                } else {
                    leg.slotWarningArrival = false;
                }
            }

            // Delay Bar
            if (state.flights_OCCMode) {
                if (dayjs(leg.departureTime).isAfter(leg.dep_sch_time)) {
                    occDelayBar = {
                        name: 'DEP_DELAY',
                        eventColor: '#B80C09',
                        startOffset: DateHelper.diff(
                            new Date(dayjs(leg.dep_sch_time).utc().format('YYYY/MM/DD HH:mm')),
                            new Date(dayjs(leg.dep_sch_time).utc().format('YYYY/MM/DD HH:mm'))
                        ),
                        endOffset: DateHelper.diff(
                            new Date(dayjs(leg.dep_sch_time).utc().format('YYYY/MM/DD HH:mm')),
                            new Date(dayjs(leg.departureTime).utc().format('YYYY/MM/DD HH:mm'))
                        ),
                    };
                    startDate = new Date(dayjs(leg.dep_sch_time).utc().format('YYYY/MM/DD HH:mm'));
                }
            }

            tmpEvents.push({
                id: leg.idFlight,
                resourceId: leg.idAircraft,
                name: leg.flightNo,
                dep_slt_time: leg.dep_slt_time,
                arr_slt_time: leg.arr_slt_time,
                dep_sch_time: leg.dep_sch_time,
                arr_sch_time: leg.arr_sch_time,
                OCCMode: state.flights_OCCMode,
                flightLabelContent: state.flights_FlightLabelContent,
                startDate: startDate,
                endDate: endDate,
                durationUnit: 'h',
                cls: flightCls,
                legInfo: leg,
                isParent: true,
                style: style,
                occDelayBar: occDelayBar,
            });
        });

        state.flights_Events = tmpEvents;
    }, // eof mut_build_Events()

    /***************************************************************************
     * mut_build_Dependencies()
     *
     * Desc: Filters the Dependencies for selected Events.
     * Note:
     *
     */
    mut_build_Dependencies: (state) => {
        const tmpDependencies = [];
        let sortedLegs = [],
            filteredViaLegs = [],
            filteredTriaLegs = [],
            tmpDependencie = {},
            foundDependencie = {};

        sortedLegs = state.flights_Legs.sort((a, b) => {
            if (a.idAircraft < b.idAircraft) {
                return -1;
            }
            if (a.idAircraft > b.idAircraft) {
                return 1;
            }
            if (dayjs(a.dep_sch_time).isAfter(dayjs(b.dep_sch_time))) {
                return 1;
            }
            if (dayjs(a.dep_sch_time).isBefore(dayjs(b.dep_sch_time))) {
                return -1;
            }
        });

        // >>> Legs => Build Dependencies ----------------- VIA/TRIA
        filteredViaLegs = sortedLegs.filter((leg) => leg.flightType === 'V'); // VIA => AB, BC || TRIA => AB, BC, CA
        filteredViaLegs.forEach((leg) => {
            if (leg.flightType === 'V') {
                // FROM AB TO BC
                foundDependencie = tmpDependencies.find((t) => t.idStartSegment === leg.idStartSegment);
                if (foundDependencie) {
                    if (leg.name === 'AB') {
                        foundDependencie.fromEvent = leg.idFlight;
                    } else if (leg.name === 'BC') {
                        foundDependencie.toEvent = leg.idFlight;
                    }
                } else {
                    if (leg.name === 'AB') {
                        tmpDependencie.fromEvent = leg.idFlight;
                    } else if (leg.name === 'BC') {
                        tmpDependencie.toEvent = leg.idFlight;
                    }
                    tmpDependencie.idStartSegment = leg.idStartSegment;
                    tmpDependencie.cls = ['sdr-arrow'];
                    tmpDependencies.push(tmpDependencie);
                    tmpDependencie = {};
                }
            }
        });

        filteredTriaLegs = sortedLegs.filter((leg) => leg.flightType === 'T'); // VIA => AB, BC || TRIA => AB, BC, CA
        filteredTriaLegs.forEach((leg) => {
            if (leg.flightType === 'T') {
                // FROM AB TO BC
                if (leg.name === 'AB') {
                    tmpDependencie = {
                        fromEvent: leg.idFlight,
                        idStartSegment: leg.idStartSegment,
                    };
                }
                if (leg.name === 'BC' && tmpDependencie && tmpDependencie.idStartSegment === leg.idStartSegment) {
                    tmpDependencie.toEvent = leg.idFlight;
                    tmpDependencie.cls = ['sdr-arrow'];

                    tmpDependencies.push(tmpDependencie);
                    tmpDependencie = null;
                }
                // FROM BC TO CA
                if (leg.name === 'BC' && !tmpDependencie) {
                    tmpDependencie = {
                        fromEvent: leg.idFlight,
                        idStartSegment: leg.idStartSegment,
                    };
                }

                if (leg.name === 'CA' && tmpDependencie && tmpDependencie.idStartSegment === leg.idStartSegment) {
                    tmpDependencie.toEvent = leg.idFlight;
                    tmpDependencie.cls = ['sdr-arrow'];

                    tmpDependencies.push(tmpDependencie);
                    tmpDependencie = null;
                }
            }
        });
        state.flights_Dependencies = tmpDependencies;
    }, // eof mut_build_Dependencies()

    /***************************************************************************
     * Mutations: Update Flights
     ***************************************************************************/
    /***************************************************************************
     * mut_call_updateFlights()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_updateFlights: (state, payload) => {
        payload.payload = payload.payload.filter((leg) => leg.name !== 'AC' && leg.name !== 'BA'); // save all

        payload.payload.forEach((flight) => {
            flight.wasEdited = true;
        });
        // >>> New Legs into flights_Legs State ------------------------
        state.flights_Legs = state.flights_Legs.filter(
            (flight) =>
                payload.payload.find((payloadflight) => payloadflight.idFlight === flight.idFlight) === undefined
        ); // delete "old" updated flights
        state.flights_Legs = state.flights_Legs.concat(payload.payload); // add "new" updated flights

        // >>> New Legs into selectedMultiEvents State ------------------------ TODO: locate code on another position?
        state.selectedMultiEvents.forEach((oldFlight) => {
            payload.payload.forEach((editedFlight) => {
                if (oldFlight.id === editedFlight.idFlight) {
                    oldFlight.legInfo = editedFlight;
                }
            });
        });

        // >>> New Legs into flights_similarFlights State ------------------------ TODO: locate code on another position?
        state.flights_similarFlights.flights.forEach((oldFlight) => {
            payload.payload.forEach((editedFlight) => {
                if (oldFlight.id === editedFlight.idFlight) {
                    oldFlight.legInfo = editedFlight;
                }
            });
        });

        // >>> Mutations ----------------------------------
        payload.context.dispatch('actn_call_getLegsInfo_warnings');
        payload.context.dispatch('actn_call_getSlotWarnings', {
            fromDate: dayjs(state.flights_StartDate).format('YYYY-MM-DDT00:00:00[Z]'),
            toDate: dayjs(state.flights_EndDate).format('YYYY-MM-DDT23:59:59[Z]'),
        });
        //payload.context.commit('mut_refresh_flight_state', payload);

        // >>> Inform Components --------------------------
        eventBus.$emit('flightswasupdated');
        eventBus.$emit('flightswascombined');
    }, // eo function mut_call_updateFlights()

    /***************************************************************************
     * mut_refresh_flight_state()
     *
     * Desc:
     * Note:
     *
     */
    mut_refresh_flight_state: (state, payload) => {
        payload.context.commit('mut_build_Events', { payload: payload });
        payload.context.commit('mut_filter_Events');
        payload.context.commit('mut_filter_Resources');
        payload.context.commit('mut_build_Dependencies');
        payload.context.commit('mut_filter_Dependencies');
        payload.context.commit('mut_calculate_Flight_Time');
    }, // eo function mut_refresh_flight_state()

    /***************************************************************************
     * Mutations: Occ / Planner Mode
     ***************************************************************************/
    /***************************************************************************
     * mut_set_OPS_Mode()
     *
     * Desc: Set OCC Mode.
     * Note:
     *
     */
    mut_set_OPS_Mode: (state, payload) => {
        state.flights_OCCMode = payload.payload;
        payload.context.commit('mut_refresh_flight_state', payload);
        //eventBus.$emit('calibratezoomlevel', state.flights_Scheduler_Preset);
    }, // eof mut_set_OPS_Mode()

    /***************************************************************************
     * mut_set_FlightLabelContent()
     *
     * Desc: Set Flight Label Content.
     * Note:
     *
     */
    mut_set_FlightLabelContent: (state, payload) => {
        state.flights_FlightLabelContent = payload.payload;
        payload.context.commit('mut_refresh_flight_state', payload);
        //eventBus.$emit('calibratezoomlevel', state.flights_Scheduler_Preset);
    }, // eof mut_set_FlightLabelContent()

    /***************************************************************************
     * mut_setflightsSchedulerPreset()
     *
     * Desc: Set Flight Scheduler Preset.
     * Note:
     *
     */
    mut_setFlightsSchedulerPreset: (state, payload) => {
        state.flights_Scheduler_Preset = payload;
        eventBus.$emit('calibratezoomlevel', state.flights_Scheduler_Preset); // emit ..
    }, // eof mut_setflightsSchedulerPreset()

    /***************************************************************************
     * Mutations: Call: Create Flights
     ***************************************************************************/
    /***************************************************************************
     * mut_call_createFlights()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_createFlights: (state, payload) => {
        // Add flag for UI Magic
        payload.payload.forEach((flight) => {
            flight.wasCreated = true;
        });

        // >>> New Legs into State ------------------------
        state.flights_Legs = state.flights_Legs.filter(
            (flight) =>
                payload.payload.find((payloadflight) => payloadflight.idFlight === flight.idFlight) === undefined
        ); // delete "old" updated flights
        state.flights_Legs = state.flights_Legs.concat(payload.payload); // add "new" updated flights
        state.flights_Legs = state.flights_Legs.filter((leg) => leg.name !== 'AC' && leg.name !== 'BA'); // filter it again

        // Add created flights to the similar flights
        // TODO: if we are in extend mode.
        // TODO: push/ build of these obejcts should be a function
        payload.payload.forEach((p) => {
            state.flights_similarFlights.flights.push({
                id: p.idFlight,
                legInfo: p,
                name: p.flightNo,
                isParent: false,
            });
        });

        // >>> Mutations ----------------------------------
        payload.context.dispatch('actn_call_getLegsInfo_warnings');
        payload.context.dispatch('actn_call_getSlotWarnings', {
            fromDate: dayjs(state.flights_StartDate).format('YYYY-MM-DDT00:00:00[Z]'),
            toDate: dayjs(state.flights_EndDate).format('YYYY-MM-DDT23:59:59[Z]'),
        });

        // >>> Inform Components --------------------------
        eventBus.$emit('flightswascreated');
    }, // eof mut_call_createFlights()

    /***************************************************************************
     * Mutations: Call: Swap Flights
     ***************************************************************************/
    /***************************************************************************
     * mut_call_swapContingents()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_swapContingents: (state, payload) => {
        state.flights_swapFlightsResponse = payload.response;
    }, // eo function mut_call_swapContingents()

    /***************************************************************************
     * Mutations: 'Selections' - Set selected Multi Events/Resources.
     ***************************************************************************/
    /***************************************************************************
     * mut_set_SelectedMultiEvents()
     *
     * Desc: Set selected Multi Events.
     * Note:
     *
     */
    mut_set_SelectedMultiEvents: (context, payload) => {
        state.selectedMultiEventsEntireRecord = []; // reset
        state.selectedMultiEvents = []; // reset

        payload.forEach((item) => {
            state.selectedMultiEventsEntireRecord.push(item); // entire record
            state.selectedMultiEvents.push(item.data); // data only
            state.selectedMultiEvents.sort((a, b) => {
                if (a.legInfo.flightNo > b.legInfo.flightNo) {
                    return 1;
                }

                if (a.legInfo.flightNo < b.legInfo.flightNo) {
                    return -1;
                }
                if (dayjs(a.legInfo.dep_sch_time).isAfter(dayjs(b.legInfo.dep_sch_time))) {
                    return 1;
                }
                if (dayjs(a.legInfo.dep_sch_time).isBefore(dayjs(b.legInfo.dep_sch_time))) {
                    return -1;
                }
            });
        });
    }, // eof mut_set_SelectedMultiEvents()

    /***************************************************************************
     * mut_call_getLegsInfo_aircraftchange()
     *
     * Desc: Set selected Multi Events.
     * Note:
     *
     */
    mut_call_getLegsInfo_aircraftchange: (state, payload) => {
        const tmpFlightsAircraftchangeFrom = [],
            tmpFlightsAircraftchangeTo = [];

        payload.payload.forEach((p) => {
            if (p.name !== 'AC' && p.name !== 'BA' && p.deleted === null) {
                if (p.idAircraftConfig === payload.aircraft.from) {
                    tmpFlightsAircraftchangeFrom.push({ legInfo: p });
                }
                if (p.idAircraftConfig === payload.aircraft.to) {
                    tmpFlightsAircraftchangeTo.push({ legInfo: p });
                }
            }
        });

        state.flights_aircraftchangeFrom = tmpFlightsAircraftchangeFrom;
        state.flights_aircraftchangeTo = tmpFlightsAircraftchangeTo;
    }, // eof mut_call_getLegsInfo_aircraftchange

    /***************************************************************************
     * mut_call_changeMultipleAircrafts()
     *
     * Desc: Resets states
     * Note:
     *
     */
    mut_call_changeMultipleAircrafts: (state) => {
        state.flights_aircraftchangeFrom = []; //rest
        state.flights_aircraftchangeTo = []; //rest
    }, // eof mut_call_changeMultipleAircrafts

    /***************************************************************************
     * Mutations: 'SeatPlan'
     ***************************************************************************/
    /***************************************************************************
     * mut_call_getSeatPlan()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_getSeatPlan: (state, payload) => {
        state.flights_seatPlan = payload.payload;
    }, // eof mut_call_getSeatPlan

    /***************************************************************************
     * Mutations: 'Movement'
     ***************************************************************************/
    /***************************************************************************
     * mut_call_getMovement()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_getMovement: (state, payload) => {
        state.flights_movement = payload.payload;
    }, // eof mut_call_getMovement

    /***************************************************************************
     * Mutations: 'Swap Logs'
     ***************************************************************************/
    /***************************************************************************
     * mut_call_swapLogs()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_swapLogs: (state, payload) => {
        state.flights_swapLogs = payload.response;
    }, // eof mut_call_swapLogs

    /***************************************************************************
     * mut_call_deleteSwapLogs()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_deleteSwapLogs: (state, payload) => {
        state.flights_swapLogs = state.flights_swapLogs.filter(
            (swapLog) => payload.response.find((swapLogResponse) => swapLogResponse.id === swapLog.id) === undefined
        );
    }, // eof mut_call_deleteSwapLogs

    /***************************************************************************
     * mut_call_getBlockHours()
     *
     * Desc:
     * Note:
     *
     */
    mut_call_getBlockHours: (state, payload) => {
        state.flights_blockHours = payload.response;
    }, // eof mut_call_getBlockHours

    /***************************************************************************
     * Mutations: 'Resets'
     ***************************************************************************/
    /***************************************************************************
     * mut_reset_SimilarFlights()
     *
     * Desc:
     * Note:
     *
     */
    mut_reset_SimilarFlights: (state) => {
        state.flights_similarFlights = { flights: [] };
    }, // eof mut_reset_SimilarFlights()
};

//-----------------------------------------------------------------------------/
// #
// #    /$$$$$$             /$$     /$$
// #   /$$__  $$           | $$    |__/
// #  | $$  \ $$  /$$$$$$$/$$$$$$   /$$  /$$$$$$  /$$$$$$$   /$$$$$$$
// #  | $$$$$$$$ /$$_____/_  $$_/  | $$ /$$__  $$| $$__  $$ /$$_____/
// #  | $$__  $$| $$       | $$    | $$| $$  \ $$| $$  \ $$|  $$$$$$
// #  | $$  | $$| $$       | $$ /$$| $$| $$  | $$| $$  | $$ \____  $$
// #  | $$  | $$|  $$$$$$$ |  $$$$/| $$|  $$$$$$/| $$  | $$ /$$$$$$$/
// #  |__/  |__/ \_______/  \___/  |__/ \______/ |__/  |__/|_______/
// #
//-----------------------------------------------------------------------------/
const actions = {
    /***************************************************************************
     * Actions: Call: Events/Resources
     ***************************************************************************/
    /***************************************************************************
     * Call::'/acm/forms/getlegsinfo
     *
     * Desc:
     * Note:
     */
    actn_call_getLegsInfo: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/forms/getlegsinfo', payload.payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        if (payload.initialCall === true) {
                            context.commit('mut_call_getLegsInfo', {
                                payload: response.data,
                                context: context,
                            });
                            // set scheduler view to 'overview' if more then 1 day is loaded
                            if (dayjs(payload.payload.from).diff(dayjs(payload.payload.to), 'days') === 0) {
                                context.dispatch('actn_setFlightsSchedulerPreset', 'dayPreset');
                            } else {
                                context.dispatch('actn_setFlightsSchedulerPreset', 'overviewPreset');
                            }
                        }
                        if (payload.initialCall === false) {
                            if (!payload.aircraftchange) {
                                context.commit('mut_call_getLegsInfo_SimilarFlights', {
                                    payload: response.data,
                                    context: context,
                                    parentFlightId: payload.parentFlightId,
                                });
                                eventBus.$emit('openNodeInTreeview', payload.parentFlightId);
                            } else if (payload.aircraftchange) {
                                context.commit('mut_call_getLegsInfo_aircraftchange', {
                                    payload: response.data,
                                    aircraft: {
                                        from: payload.payload.idAircraftConfig[0],
                                        to: payload.payload.idAircraftConfig[1],
                                    },
                                });
                            }
                        }

                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_getLegsInfo()

    /***************************************************************************
     * Call::'/acm/slots/list'
     *
     * Desc:
     * Note:
     */
    actn_call_getSlotWarnings: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        // Vue.prototype.$vp_m();
        axios
            .put('/acm/slots/list', payload)
            .then(function (response) {
                // 'OK'
                // Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_getSlotWarnings', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                // Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_getSlotWarnings()

    /***************************************************************************
     * Call::'/acm/forms/getlegsinfo/warnings'
     *
     * Desc:
     * Note:
     */
    actn_call_getLegsInfo_warnings: (context) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        // Vue.prototype.$vp_m();
        axios
            .put('/acm/forms/getlegsinfo/warnings', {
                flightNo: null,
                from: dayjs(state.flights_StartDate).format('YYYY-MM-DDT00:00:00[Z]'), //"2021-04-30T00:00:01Z"
                fromAptCode: null,
                idFlight: null,
                to: dayjs(state.flights_EndDate).format('YYYY-MM-DDT23:59:59[Z]'), //"2021-04-30T23:59:59Z"
                toAptCode: null,
                DOW: [],
                idAircraftConfig: state.flights_Resources_Filtered.map((r) => {
                    return r.idAircraft;
                }),
            })
            .then(function (response) {
                // 'OK'
                // Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_getLegsInfo_warnings', {
                            payload: response.data,
                            context: context,
                        });

                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                // Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_getLegsInfo_warnings()

    /***************************************************************************
     * Actions: Filter/Search/Dates
     ***************************************************************************/
    /***************************************************************************
     * actn_setCalendarDates
     *
     * Desc: Save the new selected date range AND trigger Call to get Leg Infos.
     * Note:
     *
     * @param {object} payload ({ params: object, dateRange: object })
     */
    actn_setCalendarDates: (context, payload) => {
        context.commit('mut_set_CalendarDates', {
            payload: payload.dateRange,
            context: context,
        });
        context.dispatch('actn_call_getLegsInfo', {
            payload: payload.params,
            initialCall: true,
        });
        context.dispatch('actn_call_getSlotWarnings', {
            fromDate: dayjs(payload.dateRange.startDate).format('YYYY-MM-DDT00:00:00[Z]'),
            toDate: dayjs(payload.dateRange.endDate).format('YYYY-MM-DDT23:59:59[Z]'),
        });
    }, // eo function actn_setCalendarDates()

    /***************************************************************************
     * actn_set_FilterTerms_DaysAndCo
     *
     * Desc: Save the new selected filter terms => Days & Co.
     * Note:
     *
     * @param {object} payload ({ filterTerms: object })
     */
    actn_set_FilterTerms_DaysAndCo: (context, payload) => {
        context.commit('mut_set_FilterTerms_DaysAndCo', {
            payload: payload.filterTerms,
            context: context,
        });

        if (payload.reloadEvents) {
            context.commit('mut_set_CalendarDates', {
                payload: payload.dateRange,
                context: context,
            });
            context.dispatch('actn_call_getLegsInfo', {
                payload: payload.params,
                initialCall: true,
            });
            // context.dispatch('actn_call_getSlots', { payload: {} });
        }
    }, // eo function actn_set_FilterTerms_DaysAndCo()

    /***************************************************************************
     * actn_set_FilterTerms_LegsAndFlightNo
     *
     * Desc: Save the new selected filter terms => Legs & FlightNo.
     * Note:
     *
     * @param {object} payload ({ filterTerms: object })
     */
    actn_set_FilterTerms_LegsAndFlightNo: (context, payload) => {
        context.commit('mut_set_FilterTerms_LegsAndFlightNo', {
            payload: payload.filterTerms,
            context: context,
        });
    }, // eo function actn_set_FilterTerms_LegsAndFlightNo()

    /***************************************************************************
     * Actions: Occ / Planner Mode
     ***************************************************************************/
    /***************************************************************************
     * actn_set_OPS_Mode
     *
     * Desc: Save the new OCC Mode.
     * Note:
     *
     * @param {boolean} payload
     */
    actn_set_OPS_Mode: (context, payload) => {
        context.commit('mut_set_OPS_Mode', { payload: payload, context: context });
    }, // eo function actn_set_OPS_Mode()

    /***************************************************************************
     * actn_set_FlightLabelContent
     *
     * Desc: Save the new Flight Label Content.
     * Note:
     *
     * @param {string} payload
     */
    actn_set_FlightLabelContent: (context, payload) => {
        context.commit('mut_set_FlightLabelContent', {
            payload: payload,
            context: context,
        });
    }, // eo function actn_set_FlightLabelContent()

    /***************************************************************************
     * actn_setFlightsSchedulerPreset()
     *
     * Desc: Save the new Scheduler Preset.
     * Note:
     *
     * @param {string} payload ('overviewPreset' | 'hourPreset' | 'dayPreset')
     */
    actn_setFlightsSchedulerPreset: (context, payload) => {
        context.commit('mut_setFlightsSchedulerPreset', payload);
    }, // eo function actn_setFlightsSchedulerPreset()

    /***************************************************************************
     * Actions: Call: Create/Update/Change Flights/Aurcrafts
     ***************************************************************************/
    /***************************************************************************
     * actn_call_createFlights()
     *
     * Desc: Create Flights.
     * Note:
     *
     * @param {string} payload ('flight create object')
     */
    actn_call_createFlights: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flightplanning/flight/create', payload.payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_createFlights', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_createFlights()

    /***************************************************************************
     * actn_call_updateFlights()
     *
     * Desc: Update Flights.
     * Note: e.g.: like Times
     *
     * @param {string} payload ('flight create object')
     */
    actn_call_updateFlights: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flightplanning/flight/update', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        //context.dispatch("actn_call_getLegsInfo", {});
                        context.commit('mut_call_updateFlights', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_updateFlights()

    /***************************************************************************
     * actn_call_deleteFlight()
     *
     * Desc: Delete Flights.
     * Note:
     *
     * @param {string} payload ('flight id')
     */
    actn_call_deleteFlight: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flightplanning/flight/delete/multi', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_deleteFlight', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_deleteFlight()

    /***************************************************************************
     * actn_call_reActivateFlight()
     *
     * Desc: Delete Flights.
     * Note:
     *
     * @param {string} payload ('flight id')
     */
    actn_call_reActivateFlight: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('acm/flightplanning/flight/undelete', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_reActicateFlight', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_reActivateFlight()

    /***************************************************************************
     * actn_call_changeAircraft()
     *
     * Desc: Chaneg Aircraft.
     * Note: SPECIAL: returns a promise so we can wait for this action to be done.
     * Note: Spinner needs to be triggered in component (currently).
     *
     * @param {string} payload
     */
    actn_call_changeAircraft: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        return new Promise((resolve) => {
            axios
                .get('/acm/flightplanning/changeaircraft/' + payload.idFlight + '/' + payload.idAircraft)
                .then(function (response) {
                    // 'OK'
                    hdrs = response.headers['air41-status'];
                    switch (hdrs) {
                        case 'OK': // 'OK'
                            if (payload.callIndex === payload.totalCalls) {
                                context.commit('mut_call_updateFlights', {
                                    payload: payload.draggedFlights,
                                    context: context,
                                });
                            }
                            break;
                        default: // 'ERROR'
                            Vue.prototype.$toasted.global.sdr_app_error({
                                message: response.headers['air41-error'],
                            });
                            break;
                    }
                })
                .catch(function (error) {
                    // 'ERROR'
                    Vue.prototype.$toasted.global.sdr_app_error({
                        message: 'Service unavailable: ' + error,
                    });
                })
                .finally(function () {
                    resolve();
                });
        });
    }, // eo function actn_call_changeAircraft()

    /***************************************************************************
     * actn_call_changeMultipleAircrafts()
     *
     * Desc: Chaneg Aircraft.
     * Note: SPECIAL: returns a promise so we can wait for this action to be done.
     * Note: Spinner needs to be triggered in component (currently).
     *
     * @param {string} payload
     */
    actn_call_changeMultipleAircrafts: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        return new Promise((resolve) => {
            Vue.prototype.$vp_m();
            axios
                .put('/acm/flightplanning/changeaircrafts', payload)
                .then(function (response) {
                    // 'OK'
                    hdrs = response.headers['air41-status'];
                    switch (hdrs) {
                        case 'OK': // 'OK'
                            context.commit('mut_call_updateFlights', {
                                payload: response.data,
                                context: context,
                            });
                            eventBus.$emit('aircraftschanged'); // emits reset of states in component / store

                            break;
                        default: // 'ERROR'
                            Vue.prototype.$toasted.global.sdr_app_error({
                                message: response.headers['air41-error'],
                            });
                            break;
                    }
                })
                .catch(function (error) {
                    // 'ERROR'
                    Vue.prototype.$toasted.global.sdr_app_error({
                        message: 'Service unavailable: ' + error,
                    });
                })
                .finally(function () {
                    Vue.prototype.$vp_um();
                    resolve();
                });
        });
    }, // eo function actn_call_changeMultipleAircrafts()

    /***************************************************************************
     * actn_call_swapContingents()
     *
     * Desc:
     * Note:
     *
     * @param {object} payload
     */
    actn_call_swapContingents: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .get(`/acm/flightplanning/swapcontingents/${payload.deletedFlightId}/${payload.newFlightId}`)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        Vue.prototype.$toasted.global.sdr_app_info({
                            message: 'Swap done',
                        });
                        context.commit('mut_call_swapContingents', {
                            payload: payload,
                            context: context,
                            response: response,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_swapContingents()

    /***************************************************************************
     * actn_call_swapFlights()
     *
     * Desc:
     * Note:
     *
     * @param {object} payload
     */
    actn_call_swapFlights: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .get(`/rebooking/changeflight/${payload.deletedFlightId}/${payload.newFlightId}`)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        Vue.prototype.$toasted.global.sdr_app_info({
                            message: 'Swap done',
                        });
                        context.commit('mut_call_swapContingents', {
                            payload: payload,
                            context: context,
                            response: response,
                        });
                        eventBus.$emit('flightswereswapped', { response: response.data });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_swapFlights()

    /***************************************************************************
     * actn_call_combineFlights()
     *
     * Desc:
     * Note:
     *
     * @param {object} payload
     */
    actn_call_combineFlights: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flightplanning/flight/reroute', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'+
                        context.commit('mut_call_createFlights', {
                            payload: response.data.newFlights,
                            context: context,
                        });
                        context.commit('mut_call_deleteFlight', {
                            payload: response.data.deletedFlights,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_combineFlights()

    /***************************************************************************
     * actn_call_blockHoursDownload()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_blockHoursDownload: (context, payload) => {
        let hdrs = '',
            url = '',
            link = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flights/hours/report/download', payload, {
                responseType: 'blob',
            })
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/csv' }));
                        link = document.createElement('a');

                        link.href = url;
                        link.setAttribute('download', 'report.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_blockHoursDownload()

    /***************************************************************************
     * actn_call_flightScheduleDownload()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_flightScheduleDownload: (context, payload) => {
        let hdrs = '',
            url = '',
            link = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flights/report', payload, { responseType: 'blob' })
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/csv' }));
                        link = document.createElement('a');

                        link.href = url;
                        link.setAttribute('download', 'FlightSchedule.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_flightScheduleDownload()

    /***************************************************************************
     * actn_call_swapLogsDownload()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_swapLogsDownload: (context) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .get('/acm/flightplanning/swaplog')
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_swapLogs', {
                            response: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_swapLogsDownload()

    /***************************************************************************
     * actn_call_deleteSwapLog()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_deleteSwapLog: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flightplanning/swaplog/delete', payload.payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_deleteSwapLogs', {
                            response: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // of actn_call_deleteSwapLog()

    /***************************************************************************
     * actn_call_deleteSwapLog()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_getBlockHours: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/acm/flights/hours/report', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_getBlockHours', {
                            response: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // of actn_call_deleteSwapLog()

    /***************************************************************************
     * actn_call_getSeatPlan()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_getSeatPlan: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/seatplan/status', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_getSeatPlan', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_getSeatPlan()

    /***************************************************************************
     * actn_call_getMovement()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_getMovement: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .get(`/pnl/mvt/${payload}`)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        context.commit('mut_call_getMovement', {
                            payload: response.data,
                            context: context,
                        });
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_getMovement()

    /***************************************************************************
     * actn_call_getMovement()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_createUpdateMovement: (context, payload) => {
        let hdrs = '';

        // >>> Send Ajax Request --------------------------
        Vue.prototype.$vp_m();
        axios
            .put('/pnl/mvt', payload)
            .then(function (response) {
                // 'OK'
                Vue.prototype.$vp_um();
                hdrs = response.headers['air41-status'];
                switch (hdrs) {
                    case 'OK': // 'OK'
                        if (response.data.flight) {
                            context.commit('mut_call_updateFlights', {
                                payload: [response.data.flight],
                                context: context,
                            });
                        }
                        break;
                    default: // 'ERROR'
                        Vue.prototype.$toasted.global.sdr_app_error({
                            message: response.headers['air41-error'],
                        });
                        break;
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eo function actn_call_getMovement()

    /***************************************************************************
     * Actions: 'Selections' - Set selected Multi Events/Resources.
     ***************************************************************************/
    /***************************************************************************
     * actn_set_SelectedMultiEvents()
     *
     * Desc: Set selected Multi Events.
     * Note: same like in 'CrewsCalendar'.
     *
     */
    actn_set_SelectedMultiEvents: (context, payload) => {
        context.commit('mut_set_SelectedMultiEvents', payload);
    }, // eof actn_set_SelectedMultiEvents()

    /***************************************************************************
     * Actions: 'Resets'
     ***************************************************************************/
    /***************************************************************************
     * actn_reset_SimilarFlights()
     *
     * Desc:
     * Note:
     *
     */
    actn_reset_SimilarFlights: (context) => {
        context.commit('mut_reset_SimilarFlights');
    }, // eof actn_reset_SimilarFlights()

    /***************************************************************************
     * actn_reset_DraggedTimesOnFlights()
     *
     * Desc:
     * Note:
     *
     */
    actn_reset_DraggedTimesOnFlights: (context, payload) => {
        context.commit('mut_call_updateFlights', {
            payload: payload,
            context: context,
        });
    }, // eof actn_reset_DraggedTimesOnFlights()

    /***************************************************************************
     * actn_reset_ChangeAircraftsState()
     *
     * Desc:
     * Note:
     *
     */
    actn_reset_ChangeAircraftsState: (context) => {
        context.commit('mut_call_changeMultipleAircrafts');
    }, // eof actn_reset_ChangeAircraftsState()

    /***************************************************************************
     * actn_reset_getSeatPlan()
     *
     * Desc:
     * Note:
     *
     */
    actn_reset_getSeatPlan: (context) => {
        context.commit('mut_call_getSeatPlan', { payload: null });
    }, // eof actn_reset_getSeatPlan()

    /***************************************************************************
     * actn_call_getSitaExport()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_getSitaExport: function (context, payload) {
        const theQuery = query({
            operation: 'db_flights_pdf',
            variables: { hasFlightLog: true },
            fields: ['status', 'message', 'downloadLink', { flights: payload.query }],
        });

        Vue.prototype.$vp_m();
        axios
            .post('https://sita-test-api.sundair.com/graphql', theQuery)
            .then(function (response) {
                if (response.status === 200) {
                    const data = response.data.data;
                    context.dispatch('actn_call_downloadSitaExport', {
                        downloadLink: data.db_flights_pdf.downloadLink,
                    });
                }
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eof actn_call_getSitaExport()

    /***************************************************************************
     * actn_call_downloadSitaExport()
     *
     * Desc:
     * Note:
     *
     */
    actn_call_downloadSitaExport: function (context, payload) {
        let url = '',
            link = '';
        axios
            .put(`https://sita-test-api.sundair.com/download/`, payload, { responseType: 'arraybuffer' })
            .then(function (response) {
                Vue.prototype.$vp_um();
                url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    })
                );
                link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', payload.downloadLink);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch(function (error) {
                // 'ERROR'
                Vue.prototype.$vp_um();
                Vue.prototype.$toasted.global.sdr_app_error({
                    message: 'Service unavailable: ' + error,
                });
            });
    }, // eof actn_call_downloadSitaExport()
};

//-----------------------------------------------------------------------------/
//-----------------------------------------------------------------------------/
//-----------------------------------------------------------------------------/
export default {
    state,
    getters,
    mutations,
    actions,
};
